'use client'

import React, { useMemo, useRef, useState } from 'react'

import Form, { FormGeneratorHandles } from '@/components/Form'
import Splash from '@/components/splash/Splash'
import Button from '@/components/UiElements/Button'
import changePasswordByAdmin from '@/services/UserService/changePasswordByAdmin'
import { User } from '@/types/user'
import { changePasswordFormItems } from './forms/userEditFormItems'

interface ChangePasswordModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ user, onClose, onSuccess }) => {
  const formRef = useRef<FormGeneratorHandles>(null)
  const [isLoading, setIsLoading] = useState(false)
  const formItems = useMemo(() => changePasswordFormItems(), [])

  const handleChangePassword = async () => {
    const errors = await formRef.current?.validate()
    if (errors && errors.length > 0) {
      return
    }

    const password = formRef.current?.getData().password
    setIsLoading(true)

    const result = await changePasswordByAdmin({
      email: user.email,
      password,
    })

    setIsLoading(false)

    if (result) {
      onSuccess()
      onClose()
    }
  }

  const handleCancel = () => {
    const password = formRef.current?.getData().password
    if (password && password.trim() !== '') {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?')
      if (!confirmed) return
    }
    onClose()
  }

  return (
    <div className="flex flex-col gap-xxxl">
      {isLoading && <Splash />}
      <form
        className="w-[500px] max-w-[calc(100vw-80px)] grid grid-cols-12 gap-md"
        autoComplete="off"
        data-form-type="other"
        onSubmit={(e) => e.preventDefault()}
      >
        <input type="text" name="hidden_username" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
        <input type="password" name="hidden_password" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
        <div className="col-span-12 text-text-md text-gray-500">
          Change password for: <span className="font-semibold text-gray-900">{user.email}</span>
        </div>
        <Form ref={formRef} items={formItems} />
      </form>

      <div className="flex justify-end gap-md">
        <Button size="lg" onClick={handleCancel} type="alt" disabled={isLoading}>
          Cancel
        </Button>
        <Button size="lg" onClick={handleChangePassword} disabled={isLoading}>
          Update Password
        </Button>
      </div>
    </div>
  )
}

export default ChangePasswordModal
